import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Phone, PhoneOff, Video, VideoOff, Mic, MicOff, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import {
  endCallSession,
  transitionCallSessionById,
  transitionCallSessionByRoom,
} from '@/governor/calls/CallsLifecycleAuthority';
import { toast } from '@/components/ui/sonner';
import { authorizeCallsRuntimeAction } from '@/governor/calls/CallsRuntimeControlAdapter';
import { observeCallsRuntimeHandler } from '@/governor/calls/CallsRuntimeObservationBridge';
import { emitCallsRuntimeEvent } from '@/lib/governorEventBus';

type CallMode = 'voice' | 'video';
type CallState = 'idle' | 'calling' | 'ringing' | 'connecting' | 'connected' | 'ended';

interface CallModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: CallMode;
  selfEmail: string;
  selfName: string;
  peerEmail: string;
  peerName: string | null;
  /** if true we initiated the call, else we're receiving */
  initiator: boolean;
  roomName?: string;
  callSessionId?: string | null;
  onConnected?: () => void;
}

const ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

const channelName = (a: string, b: string) => `call:${[a, b].sort().join('|')}`;

const CallModal: React.FC<CallModalProps> = ({
  open,
  onOpenChange,
  mode,
  selfEmail,
  selfName,
  peerEmail,
  peerName,
  initiator,
  roomName: suppliedRoomName,
  callSessionId = null,
  onConnected,
}) => {
  const roomName =
    suppliedRoomName ||
    channelName(
      selfEmail,
      peerEmail,
    );

  const emitCallEvidence =
    useCallback(
      (
        detail:
          Record<string, unknown>,
      ) => {
        return emitCallsRuntimeEvent({
          ...detail,
          stage: String(
            detail.stage ??
            detail.step ??
            'CALL_MEDIA_RUNTIME_EVENT',
          ),
          callSessionId,
          roomName,
          selfEmail,
          peerEmail,
          mode,
        } as any);
      },
      [
        callSessionId,
        mode,
        peerEmail,
        roomName,
        selfEmail,
      ],
    );
  const [state, setState] = useState<CallState>('idle');
  const [muted, setMuted] = useState(false);
  const [videoOff, setVideoOff] = useState(false);
  const [duration, setDuration] = useState(0);

  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const durationTimerRef = useRef<number | null>(null);
  const lastPersistedConnectionStateRef = useRef<string | null>(null);
  const localMediaReadyRef = useRef(false);
  const remoteMediaReadyRef = useRef(false);
  const mediaCertifiedRef = useRef(false);
  const mediaCertifiedAtRef = useRef<number | null>(null);
  const conversationCertifiedRef = useRef(false);
  const conversationCertificationTimerRef = useRef<number | null>(null);
  const sentIceCandidateCountRef = useRef(0);
  const receivedIceCandidateCountRef = useRef(0);

  const certifyMediaPath = useCallback(
    (trigger: string) => {
      const peerConnection = pcRef.current;
      const connected =
        peerConnection?.connectionState === 'connected';

      if (
        connected &&
        localMediaReadyRef.current &&
        remoteMediaReadyRef.current
      ) {
        if (mediaCertifiedRef.current) return;
        mediaCertifiedRef.current = true;
        mediaCertifiedAtRef.current = Date.now();

        if (conversationCertificationTimerRef.current) {
          window.clearTimeout(
            conversationCertificationTimerRef.current,
          );
        }

        conversationCertificationTimerRef.current =
          window.setTimeout(() => {
            const stillConnected =
              pcRef.current?.connectionState === 'connected';

            if (
              stillConnected &&
              mediaCertifiedRef.current &&
              localMediaReadyRef.current &&
              remoteMediaReadyRef.current &&
              !conversationCertifiedRef.current
            ) {
              conversationCertifiedRef.current = true;

              emitCallEvidence({
                source: 'CallModal',
                step: 'CONVERSATION_WINDOW_CERTIFIED',
                status: 'success',
                selfEmail,
                peerEmail,
                roomName,
                mode,
                stableDurationSeconds: 10,
                connectionState:
                  pcRef.current?.connectionState ?? 'unknown',
                iceConnectionState:
                  pcRef.current?.iceConnectionState ?? 'unknown',
                timestamp: new Date().toISOString(),
              });
            }
          }, 10000);

        emitCallEvidence({
          source: 'CallModal',
          step: 'MEDIA_PATH_CERTIFIED',
          status: 'success',
          trigger,
          selfEmail,
          peerEmail,
          roomName,
          mode,
          connectionState:
            peerConnection?.connectionState ?? 'unknown',
          iceConnectionState:
            peerConnection?.iceConnectionState ?? 'unknown',
          sentIceCandidates:
            sentIceCandidateCountRef.current,
          receivedIceCandidates:
            receivedIceCandidateCountRef.current,
          timestamp: new Date().toISOString(),
        });
        return;
      }

      emitCallEvidence({
        source: 'CallModal',
        step: 'MEDIA_PATH_AWAITING_REQUIREMENTS',
        status: 'pending',
        trigger,
        selfEmail,
        peerEmail,
        roomName,
        mode,
        localMediaReady: localMediaReadyRef.current,
        remoteMediaReady: remoteMediaReadyRef.current,
        connectionState:
          peerConnection?.connectionState ?? 'unknown',
        iceConnectionState:
          peerConnection?.iceConnectionState ?? 'unknown',
        timestamp: new Date().toISOString(),
      });
    },
    [mode, peerEmail, roomName, selfEmail],
  );

  const persistConnectionLifecycle = useCallback(
    async (
      lifecycleState: 'connecting' | 'connected' | 'ended',
      evidenceStep: string,
    ) => {
      if (!roomName) return;
      if (lastPersistedConnectionStateRef.current === lifecycleState) return;

      lastPersistedConnectionStateRef.current = lifecycleState;
      const observedAt = new Date().toISOString();

      try {
        const transitionResult =
          callSessionId
            ? await transitionCallSessionById(
                callSessionId,
                lifecycleState,
              )
            : await transitionCallSessionByRoom(
                roomName,
                lifecycleState,
              );

        if (!transitionResult.callSessionId) {
          emitCallEvidence({
            source: 'CallModal',
            step: 'CONNECTION_SESSION_NOT_FOUND',
            lifecycleState,
            evidenceStep,
            selfEmail,
            peerEmail,
            roomName,
            timestamp: observedAt,
          });
          return;
        }

        if (!transitionResult.ok) {
          throw new Error(
            transitionResult.error ||
              'Call lifecycle transition failed.',
          );
        }

        emitCallEvidence({
          source: 'CallModal',
          step: evidenceStep,
          lifecycleState,
          callSessionId:
            transitionResult.callSessionId,
          previousStatus:
            transitionResult.previousStatus,
          selfEmail,
          peerEmail,
          roomName,
          timestamp: observedAt,
        });
      } catch (error) {
        emitCallEvidence({
          source: 'CallModal',
          step: 'CONNECTION_SESSION_SYNC_FAILED',
          lifecycleState,
          evidenceStep,
          selfEmail,
          peerEmail,
          roomName,
          error: error instanceof Error ? error.message : String(error),
          timestamp: observedAt,
        });
      }
    },
    [
      callSessionId,
      peerEmail,
      roomName,
      selfEmail,
    ],
  );

  useEffect(() => {
    if (!open) return;

    const lifecycleState = String(state);

    if (lifecycleState === 'connecting') {
      void persistConnectionLifecycle('connecting', 'CALL_SESSION_CONNECTING');
      return;
    }

    if (lifecycleState === 'connected') {
      void persistConnectionLifecycle('connected', 'CALL_SESSION_CONNECTED');
      return;
    }

    if (
      (lifecycleState === 'ended' || lifecycleState === 'idle') &&
      (lastPersistedConnectionStateRef.current === 'connecting' ||
        lastPersistedConnectionStateRef.current === 'connected')
    ) {
      void persistConnectionLifecycle('ended', 'CALL_SESSION_ENDED');
    }
  }, [open, persistConnectionLifecycle, state]);

  useEffect(() => {
    if (!open || String(state) !== 'connecting') return;

    const timeout = window.setTimeout(() => {
      if (String(pcRef.current?.connectionState) === 'connected') return;

      emitCallEvidence({
        source: 'CallModal',
        step: 'CALL_CONNECTION_TIMEOUT',
        lifecycleState: 'connecting',
        selfEmail,
        peerEmail,
        roomName,
        reason: 'Peer connection did not reach connected state within 20 seconds.',
        timestamp: new Date().toISOString(),
      });
    }, 20000);

    return () => window.clearTimeout(timeout);
  }, [open, peerEmail, roomName, selfEmail, state]);

  useEffect(() => {
    if (!open || String(state) !== 'connected') return;

    const timeout = window.setTimeout(() => {
      if (mediaCertifiedRef.current) return;

      emitCallEvidence({
        source: 'CallModal',
        step: 'MEDIA_PATH_CERTIFICATION_TIMEOUT',
        status: 'failure',
        selfEmail,
        peerEmail,
        roomName,
        mode,
        localMediaReady: localMediaReadyRef.current,
        remoteMediaReady: remoteMediaReadyRef.current,
        connectionState:
          pcRef.current?.connectionState ?? 'unknown',
        iceConnectionState:
          pcRef.current?.iceConnectionState ?? 'unknown',
        sentIceCandidates:
          sentIceCandidateCountRef.current,
        receivedIceCandidates:
          receivedIceCandidateCountRef.current,
        reason:
          'Peer connection reached connected state without a verified remote media track within 8 seconds.',
        timestamp: new Date().toISOString(),
      });
    }, 8000);

    return () => window.clearTimeout(timeout);
  }, [mode, open, peerEmail, roomName, selfEmail, state]);

  const cleanup = useCallback(() => {
    if (durationTimerRef.current) {
      window.clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }

    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
    }

    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }

    localMediaReadyRef.current = false;
    remoteMediaReadyRef.current = false;
    if (conversationCertificationTimerRef.current) {
      window.clearTimeout(
        conversationCertificationTimerRef.current,
      );
      conversationCertificationTimerRef.current = null;
    }

    mediaCertifiedRef.current = false;
    mediaCertifiedAtRef.current = null;
    conversationCertifiedRef.current = false;
    sentIceCandidateCountRef.current = 0;
    receivedIceCandidateCountRef.current = 0;
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    if (localVideoRef.current) localVideoRef.current.srcObject = null;
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
  }, []);

  const endingRef =
    useRef(false);

  const endCall = useCallback(
    async (
      notify: boolean = true,
    ) => {
      if (endingRef.current) {
        return;
      }

      endingRef.current = true;

      observeCallsRuntimeHandler(
        'CallModal.endCall',
      );

      emitCallEvidence({
        source: 'CallModal',
        stage: 'END_REQUESTED',
        status: 'attempt',
        notifyPeer: notify,
      });

      const mediaDurationSeconds =
        mediaCertifiedAtRef.current === null
          ? 0
          : Math.max(
              0,
              Math.floor(
                (
                  Date.now() -
                  mediaCertifiedAtRef.current
                ) / 1000,
              ),
            );

      emitCallEvidence({
        source: 'CallModal',
        stage: 'CALL_CONVERSATION_ENDED',
        status:
          conversationCertifiedRef.current
            ? 'success'
            : 'ended',
        mediaCertified:
          mediaCertifiedRef.current,
        conversationCertified:
          conversationCertifiedRef.current,
        mediaDurationSeconds,
        notifyPeer: notify,
      });

      const endResult =
        callSessionId
          ? await endCallSession({
              callSessionId,
            })
          : await endCallSession({
              callerEmail:
                initiator
                  ? selfEmail
                  : peerEmail,
              receiverEmail:
                initiator
                  ? peerEmail
                  : selfEmail,
              roomName,
            });

      emitCallEvidence({
        source: 'CallModal',
        stage:
          endResult.ok
            ? 'END_SESSION_UPDATED'
            : 'END_SESSION_FAILED',
        status:
          endResult.ok
            ? 'success'
            : 'failure',
        lifecycleChanged:
          endResult.changed,
        error:
          endResult.error ||
          undefined,
      });

      if (
        notify &&
        channelRef.current
      ) {
        try {
          await channelRef.current.send({
            type: 'broadcast',
            event: 'hangup',
            payload: {
              from: selfEmail,
              callSessionId,
            },
          });
        } catch {}
      }

      setState('ended');
      cleanup();

      window.setTimeout(() => {
        onOpenChange(false);
        setState('idle');
        setDuration(0);
        endingRef.current = false;
      }, 400);
    },
    [
      callSessionId,
      cleanup,
      emitCallEvidence,
      initiator,
      onOpenChange,
      peerEmail,
      roomName,
      selfEmail,
    ],
  );

  useEffect(() => {
    if (!open) {
      cleanup();
      setState('idle');
      setDuration(0);
      setMuted(false);
      setVideoOff(false);
      return;
    }

    let cancelled = false;

    const start = async () => {
    const runtimeControl =
      authorizeCallsRuntimeAction('start', {
        source: 'call-modal',
      });

    if (!runtimeControl.allowed) {
      console.warn(
        'Calls Governor denied runtime start.',
        runtimeControl,
      );
      return;
    }

      emitCallEvidence({
      source: 'CallModal',
      stage: 'RUNTIME_START_AUTHORIZED',
      status: 'success',
      callSessionId,
      selfEmail,
      peerEmail,
      mode,
    });

    try {
        setState(initiator ? 'calling' : 'ringing');

        // 1) Get local media
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: mode === 'video',
        });
        if (cancelled) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        localStreamRef.current = stream;
        localMediaReadyRef.current =
          stream.getAudioTracks().some(track => track.readyState === 'live') &&
          (
            mode === 'voice' ||
            stream.getVideoTracks().some(track => track.readyState === 'live')
          );

        if (localVideoRef.current && mode === 'video') {
          localVideoRef.current.srcObject = stream;
        }

        emitCallEvidence({
          source: 'CallModal',
          step: 'LOCAL_MEDIA_ACQUIRED',
          status: localMediaReadyRef.current ? 'success' : 'failure',
          selfEmail,
          peerEmail,
          roomName,
          mode,
          audioTrackCount: stream.getAudioTracks().length,
          videoTrackCount: stream.getVideoTracks().length,
          timestamp: new Date().toISOString(),
        });

        // 2) Set up peer connection
        const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
        pcRef.current = pc;
        stream.getTracks().forEach(t => pc.addTrack(t, stream));

        pc.ontrack = (ev) => {
          const remoteStream = ev.streams[0];

          if (!remoteStream) {
            emitCallEvidence({
              source: 'CallModal',
              step: 'REMOTE_MEDIA_STREAM_MISSING',
              status: 'failure',
              selfEmail,
              peerEmail,
              roomName,
              mode,
              trackKind: ev.track.kind,
              timestamp: new Date().toISOString(),
            });
            return;
          }

          if (mode === 'video' && remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = remoteStream;
          }

          if (mode === 'voice' && remoteAudioRef.current) {
            remoteAudioRef.current.srcObject = remoteStream;
          }

          const hasLiveAudio = remoteStream
            .getAudioTracks()
            .some(track => track.readyState === 'live');
          const hasRequiredVideo =
            mode === 'voice' ||
            remoteStream
              .getVideoTracks()
              .some(track => track.readyState === 'live');

          remoteMediaReadyRef.current =
            hasLiveAudio && hasRequiredVideo;

          emitCallEvidence({
            source: 'CallModal',
            step: 'REMOTE_MEDIA_TRACK_RECEIVED',
            status: remoteMediaReadyRef.current ? 'success' : 'pending',
            selfEmail,
            peerEmail,
            roomName,
            mode,
            trackKind: ev.track.kind,
            audioTrackCount: remoteStream.getAudioTracks().length,
            videoTrackCount: remoteStream.getVideoTracks().length,
            timestamp: new Date().toISOString(),
          });

          certifyMediaPath('remote-track');
        };

        pc.onconnectionstatechange = () => {
          if (pc.connectionState === 'connected') {
        observeCallsRuntimeHandler('CallModal.connectMedia');

        emitCallEvidence({
          source: 'CallModal',
          stage: 'PEER_CONNECTION_CONNECTED',
          status: 'success',
          callSessionId,
          selfEmail,
          peerEmail,
          mode,
          connectionState: pc.connectionState,
        });

            setState('connected');
            onConnected?.();
            certifyMediaPath('peer-connected');
            if (!durationTimerRef.current) {
              const startTime = Date.now();
              durationTimerRef.current = window.setInterval(() => {
                setDuration(Math.floor((Date.now() - startTime) / 1000));
              }, 1000);
            }
          } else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        emitCallEvidence({
          source: 'CallModal',
          stage: 'PEER_CONNECTION_FAILED',
          status: 'failure',
          callSessionId,
          selfEmail,
          peerEmail,
          mode,
          connectionState: pc.connectionState,
          reason: 'Peer connection failed or disconnected',
        });

            toast.error('Call connection lost');
            void endCall(false);
          }
        };

        pc.oniceconnectionstatechange = () => {
          emitCallEvidence({
            source: 'CallModal',
            step: 'ICE_CONNECTION_STATE_CHANGED',
            status:
              pc.iceConnectionState === 'failed'
                ? 'failure'
                : 'observed',
            selfEmail,
            peerEmail,
            roomName,
            mode,
            iceConnectionState: pc.iceConnectionState,
            timestamp: new Date().toISOString(),
          });
        };

        // 3) Signaling via Supabase realtime broadcast
        const channel = supabase.channel(channelName(selfEmail, peerEmail), {

          config: { broadcast: { self: false } },
        });
        channelRef.current = channel;

        pc.onicecandidate = (ev) => {
          if (ev.candidate) {
            sentIceCandidateCountRef.current += 1;

            emitCallEvidence({
              source: 'CallModal',
              step: 'ICE_CANDIDATE_SENT',
              status: 'success',
              selfEmail,
              peerEmail,
              roomName,
              mode,
              candidateCount: sentIceCandidateCountRef.current,
              timestamp: new Date().toISOString(),
            });

            channel.send({
              type: 'broadcast',
              event: 'ice',
              payload: { from: selfEmail, candidate: ev.candidate },
            });
          }
        };

        channel
          .on('broadcast', { event: 'offer' }, async ({ payload }) => {
            if (payload.from === selfEmail) return;
            if (initiator) return;
            setState('connecting');
            await pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));
        try {
          emitCallEvidence({
            source: 'CallModal',
            step: 'REMOTE_DESCRIPTION_SET',
            selfEmail,
            peerEmail,
            roomName,
            timestamp: new Date().toISOString(),
          });
        } catch {}

            const answer = await pc.createAnswer();
        try {
          emitCallEvidence({
            source: 'CallModal',
            step: 'ANSWER_CREATED',
            selfEmail,
            peerEmail,
            roomName,
            timestamp: new Date().toISOString(),
          });
        } catch {}

            await pc.setLocalDescription(answer);
        try {
          emitCallEvidence({
            source: 'CallModal',
            step: 'ANSWER_LOCAL_DESCRIPTION_SET',
            selfEmail,
            peerEmail,
            roomName,
            timestamp: new Date().toISOString(),
          });
        } catch {}

            channel.send({
              type: 'broadcast',
              event: 'answer',
              payload: { from: selfEmail, sdp: answer },
            });
          })
          .on('broadcast', { event: 'answer' }, async ({ payload }) => {
            if (payload.from === selfEmail) return;
            setState('connecting');
            if (pc.currentRemoteDescription) return;
            await pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));
          })
          .on('broadcast', { event: 'ice' }, async ({ payload }) => {
            if (payload.from === selfEmail) return;
            try {
              await pc.addIceCandidate(new RTCIceCandidate(payload.candidate));
              receivedIceCandidateCountRef.current += 1;
        try {
          emitCallEvidence({
            source: 'CallModal',
            step: 'ICE_RECEIVED_ADDED',
            status: 'success',
            selfEmail,
            peerEmail,
            roomName,
            mode,
            candidateCount: receivedIceCandidateCountRef.current,
            timestamp: new Date().toISOString(),
          });
        } catch {}

            } catch {
              /* ignore */
            }
          })
          .on('broadcast', { event: 'hangup' }, ({ payload }) => {
            if (payload.from === selfEmail) return;
            toast(`${peerName || peerEmail} ended the call`);
            void endCall(false);
          })
          .on('broadcast', { event: 'ready' }, async ({ payload }) => {
            if (payload.from === selfEmail) return;
            if (!initiator) return;
            // Peer has joined the channel & is ready → send offer
            setState('connecting');
            const offer = await pc.createOffer();
        try {
          emitCallEvidence({
            source: 'CallModal',
            step: 'OFFER_CREATED',
            selfEmail,
            peerEmail,
            roomName,
            timestamp: new Date().toISOString(),
          });
        } catch {}

            await pc.setLocalDescription(offer);
        try {
          emitCallEvidence({
            source: 'CallModal',
            step: 'OFFER_LOCAL_DESCRIPTION_SET',
            selfEmail,
            peerEmail,
            roomName,
            timestamp: new Date().toISOString(),
          });
        } catch {}

            channel.send({
              type: 'broadcast',
              event: 'offer',
              payload: { from: selfEmail, sdp: offer, mode },
            });
          })
          .subscribe(async (status) => {
            if (status === 'SUBSCRIBED') {
              // announce presence — triggers the initiator to send the offer
              channel.send({
                type: 'broadcast',
                event: 'ready',
                payload: { from: selfEmail, mode },
              });
            }
          });
      } catch (err) {
      emitCallEvidence({
        source: 'CallModal',
        stage: 'CALL_RUNTIME_START_FAILED',
        status: 'failure',
        selfEmail,
        peerEmail,
        mode,
        error:
          err instanceof Error
            ? err.message
            : String(err),
      });


        toast.error('Could not start call', {
          description: err instanceof Error ? err.message : 'Check microphone/camera permissions',
        });
        void endCall(false);
      }
    };

    start();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleMute = () => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const next = !muted;
    stream.getAudioTracks().forEach(t => (t.enabled = !next));
    setMuted(next);
  };

  const toggleVideo = () => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const next = !videoOff;
    stream.getVideoTracks().forEach(t => (t.enabled = !next));
    setVideoOff(next);
  };

  const fmtDuration = (s: number) => {
    const m = Math.floor(s / 60).toString().padStart(2, '0');
    const r = (s % 60).toString().padStart(2, '0');
    return `${m}:${r}`;
  };

  const statusLabel =
    state === 'calling' ? 'Calling…' :
    state === 'ringing' ? 'Incoming call…' :
    state === 'connecting' ? 'Connecting…' :
    state === 'connected' ? fmtDuration(duration) :
    state === 'ended' ? 'Call ended' : '';

  const initials = (name: string | null, email: string) => {
    const src = (name && name.trim()) || email;
    const parts = src.split(/[\s@._-]+/).filter(Boolean);
    return (parts[0]?.[0] || '?').toUpperCase() + (parts[1]?.[0] || '').toUpperCase();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) void endCall();
        else onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-3xl p-0 overflow-hidden bg-zinc-900 border-zinc-800 h-[85vh] flex flex-col">
        {/* Visually-hidden a11y title/description so Radix can announce
            this dialog to screen readers without touching the visible UI. */}
        <DialogTitle className="sr-only">
          {mode === 'video' ? 'Video call' : 'Voice call'} with {peerName || peerEmail}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {statusLabel || 'Connecting to your family member.'}
        </DialogDescription>
        <audio
          ref={remoteAudioRef}
          autoPlay
          playsInline
          className="hidden"
        />
        <div className="relative flex-1 bg-gradient-to-br from-zinc-900 via-zinc-800 to-black overflow-hidden">
          {/* Remote video (or avatar for voice call) */}
          {mode === 'video' ? (
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover bg-black"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="w-32 h-32 mx-auto rounded-full bg-gradient-to-br from-amber-700 to-green-800 flex items-center justify-center text-amber-50 font-serif font-bold text-5xl shadow-2xl mb-4">
                  {initials(peerName, peerEmail)}
                </div>
                <div className="text-white font-serif text-2xl">{peerName || peerEmail.split('@')[0]}</div>
                <div className="text-zinc-400 text-sm mt-1">{peerEmail}</div>
              </div>
            </div>
          )}

          {/* Local preview */}
          {mode === 'video' && (
            <div className="absolute top-4 right-4 w-32 md:w-48 aspect-[3/4] md:aspect-video rounded-lg overflow-hidden border-2 border-white/30 shadow-xl bg-black">
              <video ref={localVideoRef} autoPlay muted playsInline className="w-full h-full object-cover" />
              {videoOff && (
                <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
                  <VideoOff className="w-6 h-6 text-white/60" />
                </div>
              )}
            </div>
          )}

          {/* Header status */}
          <div className="absolute top-0 left-0 right-0 p-4 bg-gradient-to-b from-black/70 to-transparent">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-700 to-green-800 flex items-center justify-center text-amber-50 font-bold shadow">
                {initials(peerName, peerEmail)}
              </div>
              <div>
                <div className="text-white font-semibold">{peerName || peerEmail.split('@')[0]}</div>
                <div className="text-xs text-zinc-300 flex items-center gap-1.5">
                  {(state === 'calling' || state === 'connecting' || state === 'ringing') && (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  )}
                  {state === 'connected' && (
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                  )}
                  {statusLabel}
                </div>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
            <div className="flex items-center justify-center gap-4">
              <Button
                onClick={toggleMute}
                size="icon"
                className={`rounded-full w-14 h-14 ${muted ? 'bg-white text-zinc-900 hover:bg-zinc-200' : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur'}`}
              >
                {muted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </Button>

              {mode === 'video' && (
                <Button
                  onClick={toggleVideo}
                  size="icon"
                  className={`rounded-full w-14 h-14 ${videoOff ? 'bg-white text-zinc-900 hover:bg-zinc-200' : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur'}`}
                >
                  {videoOff ? <VideoOff className="w-5 h-5" /> : <Video className="w-5 h-5" />}
                </Button>
              )}

              <Button
                onClick={() => void endCall()}
                size="icon"
                className="rounded-full w-16 h-16 bg-red-600 hover:bg-red-700 text-white shadow-xl"
              >
                <PhoneOff className="w-6 h-6" />
              </Button>
            </div>
            <p className="text-center text-[11px] text-zinc-400 mt-3">
              {mode === 'video' ? 'Video call' : 'Voice call'} · End-to-end peer connection
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CallModal;
